import { couldBe } from "./autopilot.js";
import { groupAsksItsOwn, type CustomerRoute } from "./customers.js";

/**
 * A group's letters, and which one a company gets
 * ([38](../../../docs/38-customers-by-group.md) step 2, [ADR 0068](../../../docs/adr/0068-customers-is-organised-by-group-and-joogoos-runs-each-group-inside-the-owner-s-limits.md) point 3).
 *
 * A group has several letters, each with a rule over what is already known about a company. They are read top to
 * bottom, the first whose rule matches wins, and **the last has no rule**: whatever its row says, it is the letter
 * for everyone the ones above it did not take, so no company of a group is ever left without a letter by a rule.
 *
 * A rule only reads what Joogoos already knows. A fact it does not know is not a match: a company whose size nobody
 * said falls through a *11 to 50 people* letter to the next one, rather than being guessed into it. The site's
 * language is not here because nothing reads it yet; a rule that cannot be checked would be a rule that lies.
 */

/** How many follow-ups a letter may have: every language's frame writes two (`letterFrame().followUps`). */
export const MAX_FOLLOW_UPS = 2;

export interface LetterRule {
  /** Two-letter countries; the company's must be one of them. */
  countries?: string[] | undefined;
  /** Words, any of which must appear in its trade (a list's *industry*) or in what was read on its site. */
  kinds?: string[] | undefined;
  /** Words, any of which must appear in the role of the person a list named. */
  roles?: string[] | undefined;
  /** People working there, from a list's *employees*: at least this many, at most that many. */
  sizeMin?: number | null | undefined;
  sizeMax?: number | null | undefined;
}

/** What a rule is read against: only what Joogoos already has about the company. */
export interface WhatWeKnow {
  country: string;
  industry?: string | null;
  employees?: string | null;
  role?: string | null;
  /** The facts read on its site, as the evidence sheet says them. */
  facts?: readonly string[];
}

/**
 * The lower end of what a rate could honestly be, at 90% confidence (Wilson): the other half of `couldBe`.
 */
export function atLeast(replies: number, letters: number, z = 1.2816): number {
  if (letters <= 0) return 0;
  const p = replies / letters;
  const z2 = z * z;
  const centre = p + z2 / (2 * letters);
  const spread = z * Math.sqrt((p * (1 - p)) / letters + z2 / (4 * letters * letters));
  return Math.max(0, (centre - spread) / (1 + z2 / letters));
}

/** How many letters each version has gone out on before a test may be called at all. */
export const TEST_AFTER = 20;

/**
 * Which of two versions of a letter is **clearly** ahead (38 step 5), or null while the test cannot honestly be called.
 *
 * Clearly: each has gone out on `TEST_AFTER` letters, and the worst the leader's rate could honestly be is still
 * better than the best the other's could. Anything less is a coin still in the air, and calling it would teach the
 * owner something that is not true. Answers are the measure, because that is what a letter is for.
 */
export function versionAhead(a: { written: number; answered: number }, b: { written: number; answered: number }): "a" | "b" | null {
  if (a.written < TEST_AFTER || b.written < TEST_AFTER) return null;
  if (atLeast(a.answered, a.written) > couldBe(b.answered, b.written)) return "a";
  if (atLeast(b.answered, b.written) > couldBe(a.answered, a.written)) return "b";
  return null;
}

/**
 * Which of a letter and its version a company gets: the one with fewer companies so far, the letter itself on a
 * tie — so the two stay within one of each other, which is what 50/50 means for a small business's few letters.
 */
export function splitBetween<L extends { companies: number }>(letter: L, version: L | undefined): L {
  return version && version.companies < letter.companies ? version : letter;
}

/** A rule with nothing in it, which matches every company. */
export function ruleIsEmpty(rule: LetterRule): boolean {
  return !rule.countries?.length && !rule.kinds?.length && !rule.roles?.length
    && rule.sizeMin == null && rule.sizeMax == null;
}

/** The number a list's *employees* says, by its first figure: `11-50` is 11, `250+` is 250. Null when it says none. */
export function headcount(employees: string | null | undefined): number | null {
  const m = (employees ?? "").replace(/[,\s](?=\d{3})/g, "").match(/\d+/);
  return m ? Number(m[0]) : null;
}

const has = (text: string, words: readonly string[]) => {
  const t = text.toLowerCase();
  return words.some((w) => w.trim() && t.includes(w.trim().toLowerCase()));
};

/** Whether a rule takes this company. Every part the rule says must hold; an unknown fact holds none. */
export function ruleMatches(rule: LetterRule, company: WhatWeKnow): boolean {
  if (rule.countries?.length && !rule.countries.includes(company.country)) return false;
  if (rule.kinds?.length && !has([company.industry ?? "", ...(company.facts ?? [])].join("\n"), rule.kinds)) return false;
  if (rule.roles?.length && !has(company.role ?? "", rule.roles)) return false;
  if (rule.sizeMin != null || rule.sizeMax != null) {
    const n = headcount(company.employees);
    if (n === null) return false;
    if (rule.sizeMin != null && n < rule.sizeMin) return false;
    if (rule.sizeMax != null && n > rule.sizeMax) return false;
  }
  return true;
}

/**
 * The letter a company of this group gets: the first, in order, whose rule takes it, and the last for everyone else.
 * Undefined only for a group with no letter at all, which a group never is once it is saved.
 */
export function letterFor<L extends { position: number; rule: LetterRule; versionOf?: string | null }>(letters: readonly L[], company: WhatWeKnow): L | undefined {
  // A second version is not in the order: it shares its letter's companies (`splitBetween`).
  const ordered = letters.filter((l) => !l.versionOf).sort((a, b) => a.position - b.position);
  const last = ordered[ordered.length - 1];
  return ordered.slice(0, -1).find((l) => ruleMatches(l.rule, company)) ?? last;
}

/**
 * What a letter offers, asks and is written in: its own words where it has them, the usual letter's in Settings
 * where not. What `groupLetter` did for a group, done for one of its letters.
 *
 * The one rule that is not a fallback: a group reached through another business never borrows the business's ask,
 * because that ask is the wrong sentence for it (`groupAsksItsOwn`, 23 §two kinds).
 */
export function letterWords(
  letter: { offer: string; ask: string; language: string } | undefined,
  route: CustomerRoute | undefined,
  settings: { offer: string; ask: string; languages: readonly string[] },
): { offer: string; ask: string; language: string } {
  const own = (t: string | undefined) => (t ?? "").trim();
  const ask = route && groupAsksItsOwn(route) ? own(letter?.ask) : (own(letter?.ask) || settings.ask.trim());
  return {
    offer: own(letter?.offer) || settings.offer.trim(),
    ask,
    language: own(letter?.language) || settings.languages[0] || "en",
  };
}

/** A rule in words, as the Letters tab shows it above the letter it chooses. */
export function ruleSays(rule: LetterRule, countryName: (code: string) => string = (c) => c): string {
  const parts: string[] = [];
  if (rule.kinds?.length) parts.push(rule.kinds.join(" or "));
  if (rule.countries?.length) parts.push(`in ${rule.countries.map((c) => countryName(c)).join(" or ")}`);
  if (rule.sizeMin != null && rule.sizeMax != null) parts.push(`${rule.sizeMin} to ${rule.sizeMax} people`);
  else if (rule.sizeMin != null) parts.push(`${rule.sizeMin} people or more`);
  else if (rule.sizeMax != null) parts.push(`up to ${rule.sizeMax} people`);
  if (rule.roles?.length) parts.push(`to the ${rule.roles.join(" or ")}`);
  return parts.length ? parts.join(", ") : "every company";
}

/** A rule as it is kept: trimmed, de-duplicated, bounded. Anything else in the object is dropped. */
export function cleanRule(rule: LetterRule | null | undefined): LetterRule {
  const words = (w: readonly string[] | undefined) =>
    [...new Set((w ?? []).map((x) => x.trim().slice(0, 60)).filter((x) => x.length >= 2))].slice(0, 20);
  const n = (v: number | null | undefined) => (typeof v === "number" && Number.isFinite(v) && v >= 0 ? Math.min(Math.trunc(v), 1_000_000) : null);
  const out: LetterRule = {};
  const countries = [...new Set((rule?.countries ?? []).map((c) => c.trim().toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c)))].slice(0, 40);
  if (countries.length) out.countries = countries;
  const kinds = words(rule?.kinds);
  if (kinds.length) out.kinds = kinds;
  const roles = words(rule?.roles);
  if (roles.length) out.roles = roles;
  const min = n(rule?.sizeMin);
  const max = n(rule?.sizeMax);
  if (min !== null) out.sizeMin = min;
  if (max !== null) out.sizeMax = min !== null && max < min ? min : max;
  return out;
}
